/** @format */

import {Box, Button} from '@mui/material';
import {styled} from '@mui/material/styles';

const PREFIX = 'StubWidget';

export const stubWidgetClasses = {
  headerRow: `${PREFIX}-headerRow`,
  headerLeftGroup: `${PREFIX}-headerLeftGroup`,
  categoryIconsBox: `${PREFIX}-categoryIconsBox`,
  iconLabel: `${PREFIX}-iconLabel`,
  titleText: `${PREFIX}-titleText`,
  badge: `${PREFIX}-badge`,
  badgeIcon: `${PREFIX}-badgeIcon`,
  badgeText: `${PREFIX}-badgeText`,
  bodyRow: `${PREFIX}-bodyRow`,
  infoColumn: `${PREFIX}-infoColumn`,
  acceleratedLabel: `${PREFIX}-acceleratedLabel`,
  votesText: `${PREFIX}-votesText`,
  divider: `${PREFIX}-divider`,
  votedBlock: `${PREFIX}-votedBlock`,
  votedIcon: `${PREFIX}-votedIcon`,
  votedText: `${PREFIX}-votedText`,
};

const classes = stubWidgetClasses;

export const StubWidgetRoot = styled(Box, {
  shouldForwardProp: prop => prop !== 'color',
})<{color: string}>(({theme, color}) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  padding: theme.spacing(2),
  borderRadius: theme.spacing(2),
  overflow: 'hidden',
  position: 'relative',

  '&::before': {
    content: '""',
    position: 'absolute',
    inset: 0,
    backgroundColor: color,
    opacity: 0.2,
    borderRadius: 'inherit',
  },

  [`& .${classes.headerRow}`]: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
  },

  [`& .${classes.headerLeftGroup}`]: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },

  [`& .${classes.badge}`]: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    padding: theme.spacing(0.5, 1),
    borderRadius: '9px',
    backgroundColor: theme.palette.semantic_dark?.primary.main,
    color: theme.palette.semantic_dark?.primary.on,
    whiteSpace: 'nowrap',
  },

  [`& .${classes.badgeIcon}`]: {
    fontSize: '14px',
  },

  [`& .${classes.badgeText}`]: {
    fontSize: '12px',
    fontWeight: 500,
    lineHeight: '14px',
  },

  [`& .${classes.categoryIconsBox}`]: {
    display: 'flex',
    flexDirection: 'column',
    height: '24px',
    color: theme.palette.semantic_dark?.surface.onSurfaceMuted,
  },

  [`& .${classes.iconLabel}`]: {
    fontSize: '12px',
    lineHeight: '14px',
    letterSpacing: '0.12px',
  },

  [`& .${classes.titleText}`]: {
    fontSize: '20px',
    lineHeight: '24px',
    color: theme.palette.semantic_dark?.surface.onSurface,
  },

  [`& .${classes.bodyRow}`]: {
    display: 'flex',
    gap: theme.spacing(2),
    alignItems: 'flex-start',
  },

  [`& .${classes.infoColumn}`]: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },

  [`& .${classes.acceleratedLabel}`]: {
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '16px',
    color: theme.palette.semantic_dark?.surface.onSurface,
  },

  [`& .${classes.votesText}`]: {
    fontSize: '14px',
    lineHeight: '16px',
    color: theme.palette.semantic_dark?.surface.onSurface,
  },

  [`& .${classes.divider}`]: {
    width: '1px',
    alignSelf: 'stretch',
    backgroundColor: theme.palette.semantic_dark?.surface.surface2,
  },

  [`& .${classes.votedBlock}`]: {
    display: 'flex',
    alignItems: 'center',
    borderRadius: theme.spacing(2),
    color: theme.palette.semantic_dark?.surface.onSurface,
  },

  [`& .${classes.votedIcon}`]: {
    fontSize: '28px',
  },

  [`& .${classes.votedText}`]: {
    fontSize: '12px',
    fontWeight: 500,
    lineHeight: '14px',
  },
}));

export const VoteButton = styled(Button)(({theme}) => ({
  textTransform: 'none',
  fontSize: '14px',
  fontWeight: 400,
  lineHeight: '16px',
  letterSpacing: 0,
  minWidth: 'unset',
  padding: theme.spacing(2, 4),
  backgroundColor: theme.palette.semantic_dark?.surface.surface2,
  color: theme.palette.semantic_dark?.surface.onSurface,
  borderRadius: theme.spacing(2),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(2),
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  '&:hover': {
    backgroundColor: theme.palette.semantic_dark?.surface.surface3,
  },
}));
